import React from "react";
import { Award, Building2, GraduationCap, Sparkles } from "lucide-react";
import { useSiteContent } from "../data/SiteContentContext";

const DEFAULT_STAFF_IMAGES = [
  { src: "/staff/img1.jpg", alt: "BTW counter team member at the register", tall: true },
  { src: "/staff/img3.jpg", alt: "BTW kitchen team prepping food" },
  { src: "/staff/img2.jpg", alt: "Local youth program at BTW" },
  { src: "/staff/img5.jpg", alt: "BTW staff in action", tall: true },
];

const PARTNERS = [
  { short: "NYC DOE", label: "Dept. of Education" },
  { short: "SYEP", label: "Summer Youth Employment", accent: "#A3D900" },
  { short: "MWBE", label: "Minority & Women BE" },
  { short: "NYC SBS", label: "Small Business Services" },
  { short: "VOYAGES", label: "Internships", accent: "#5FBDD6" },
  { short: "CO-OP TECH", label: "Culinary Programs" },
];

const CERTS = [
  {
    icon: Award,
    label: "NYC Certified MWBE",
    sub: "Minority and Women Led",
    code: "MWCERT2022-751",
  },
  {
    icon: Building2,
    label: "NYC Vendor Approved",
    sub: "Approved supplier for NYC agencies",
    code: "VS00073066",
  },
  {
    icon: GraduationCap,
    label: "DOE Vendor Approved",
    sub: "Trusted by NYC public schools",
    code: "FRE956166",
  },
];

export default function Community() {
  const { content } = useSiteContent();
  const c = content?.community || {};
  const eyebrow = c.eyebrow || "Community · Culture · Opportunity";
  const headlinePre = c.headline_pre || "Built by the";
  const headlineHl = c.headline_hl || "Community.";
  const lead = c.lead || "Over 80% of our team are interns, culinary school students, or graduates of local workforce programs.";
  const body = c.body || "We believe in creating real opportunities. BTW partners with programs like Summer Youth Employment and local workforce initiatives to train, hire, and develop the next generation of talent in our community.";
  const quote = c.quote || "From training to employment — we invest in people, not just positions.";
  const photosSrc = (c.photos && c.photos.length ? c.photos : DEFAULT_STAFF_IMAGES);
  // Apply tall pattern (positions 0 and 3) to match the layout
  const STAFF_IMAGES = photosSrc.slice(0, 4).map((p, i) => ({
    src: p.src,
    alt: p.alt || `BTW community photo ${i + 1}`,
    tall: i === 0 || i === 3,
  }));

  return (
    <section
      id="community"
      data-testid="community-section"
      className="bg-[#0B0B0B] text-white py-12 sm:py-24"
    >
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
        {/* Mobile-first: title block FIRST on mobile (lg moves to right side) */}
        <div className="order-1 lg:order-2 lg:col-start-8 lg:col-span-5">
          <p
            className="text-xs sm:text-sm font-extrabold tracking-[0.4em] uppercase"
            style={{ color: "#A3D900" }}
          >
            {eyebrow}
          </p>
          <h2 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl uppercase leading-[0.95] tracking-tight">
            {headlinePre}
            <br />
            <span style={{ color: "#5FBDD6" }}>{headlineHl}</span>
          </h2>
        </div>

        {/* Photo collage */}
        <div className="order-2 lg:order-1 lg:col-start-1 lg:col-span-7 lg:row-span-2" data-testid="community-photos">
          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            {STAFF_IMAGES.map((img, i) => (
              <figure
                key={img.src}
                data-testid={`community-photo-${i + 1}`}
                className={`relative overflow-hidden rounded-2xl bg-white/5 border border-white/10 ${
                  img.tall ? "aspect-[3/4]" : "aspect-[4/5]"
                } ${i === 1 ? "lg:translate-y-8" : ""} ${
                  i === 3 ? "lg:translate-y-8" : ""
                }`}
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0B]/60 via-transparent to-transparent pointer-events-none" />
              </figure>
            ))}
          </div>

          {/* small callout */}
          <div
            className="mt-8 inline-flex items-start gap-3 rounded-2xl border border-white/10 bg-white/[0.04] px-5 py-4 max-w-md"
            data-testid="community-callout"
          >
            <span
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full"
              style={{
                background: "rgba(163,217,0,0.12)",
                color: "#A3D900",
              }}
            >
              <Sparkles size={18} />
            </span>
            <p className="text-sm sm:text-base text-white/85 leading-relaxed font-medium">
              "{quote}"
            </p>
          </div>
        </div>

        {/* Body + certs (mobile: 3rd / desktop: bottom-right under headline) */}
        <div className="order-3 lg:order-3 lg:col-start-8 lg:col-span-5">
          <p className="text-white/85 text-lg sm:text-xl font-semibold leading-snug max-w-md">
            {lead}
          </p>
          <p className="mt-4 text-white/65 text-sm sm:text-base leading-relaxed max-w-lg">
            {body}
          </p>

          {/* Certifications */}
          <div className="mt-10">
            <h3 className="font-display text-sm uppercase tracking-[0.3em] text-white/80">
              Certifications
            </h3>
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="community-certs">
              {CERTS.map(({ icon: Icon, label, sub, code }) => (
                <div
                  key={label}
                  data-testid={`cert-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="rounded-xl border border-white/10 bg-white/[0.03] hover:border-[#A3D900]/40 hover:bg-white/[0.06] transition-colors p-4 flex items-center gap-4"
                >
                  <div className="flex-1 min-w-0">
                    <span
                      className="inline-flex h-10 w-10 items-center justify-center rounded-lg"
                      style={{
                        background: "rgba(30,107,255,0.12)",
                        color: "#5FBDD6",
                      }}
                    >
                      <Icon size={20} />
                    </span>
                    <div className="mt-3 font-display text-sm uppercase tracking-wider leading-tight">
                      {label}
                    </div>
                    <p className="mt-1 text-[11px] text-white/55 leading-snug">
                      {sub}
                    </p>
                  </div>
                  {code && (
                    <div
                      className="shrink-0 text-right"
                      data-testid={`cert-code-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    >
                      <div className="font-display text-[10px] tracking-[0.24em] uppercase text-[#A3D900]">#</div>
                      <div className="font-display text-base sm:text-lg lg:text-xl tracking-[0.06em] uppercase text-white leading-none whitespace-nowrap">
                        {code}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <p className="mt-5 text-xs sm:text-sm text-white/50">
              Trusted by New York City agencies, schools, and community
              programs.
            </p>
          </div>
        </div>
      </div>

      {/* Partners strip */}
      <div className="mt-10 sm:mt-16 max-w-[1400px] mx-auto px-5 sm:px-8" data-testid="partners-strip">
        <div className="flex items-center gap-4 sm:gap-6">
          <span className="h-px flex-1 bg-white/10" />
          <p className="text-[10px] sm:text-xs font-extrabold tracking-[0.4em] uppercase text-white/50 whitespace-nowrap">
            Trusted Partners & Programs
          </p>
          <span className="h-px flex-1 bg-white/10" />
        </div>

        <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-4 gap-y-6 items-center">
          {PARTNERS.map((p) => (
            <div
              key={p.label}
              data-testid={`partner-${p.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="group flex flex-col items-center justify-center text-center px-2 py-3 opacity-60 hover:opacity-100 transition-opacity"
            >
              <div
                className="font-display text-2xl sm:text-3xl uppercase tracking-tight leading-none"
                style={{ color: p.accent || "#FFFFFF" }}
              >
                {p.short}
              </div>
              <div className="mt-1.5 text-[10px] sm:text-[11px] tracking-[0.18em] uppercase text-white/55 leading-tight">
                {p.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
